import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from './firebase-config';
import { AuthProvider, useAuth } from './context/AuthContext';

import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import AuthGuard from './components/auth/AuthGuard';
// PlacementQuiz and CoursePlayer archived
// InstructorChat archived — /chat now redirects to /v2-course
import SessionReport from './pages/SessionReport';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import LandingPage from './pages/LandingPage';
import StartPage from './pages/StartPage';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AboutPage from './pages/AboutPage';
import LoadingSpinner from './components/shared/LoadingSpinner';
import Solutions from './components/Solutions';
import PurgatoryPage from './pages/PurgatoryPage';
import IPVerificationModal from './components/auth/IPVerificationModal';
import VideoTest from './pages/VideoTest';
import BetaTosPage from './pages/BetaTosPage';
import SecurityDoctrinePage from './pages/SecurityDoctrinePage';
import ProfileCreationPage from './pages/ProfileCreationPage';
import HousekeepingInfoPage from './pages/HousekeepingInfoPage';
import MicroAssessmentPage from './pages/MicroAssessmentPage';
import PlacementAssessmentPage from './pages/PlacementAssessmentPage';
import PlacementResultsPage from './pages/PlacementResultsPage';
import DayOnePage from './pages/DayOnePage';

import V2CoursePage from './pages/V2CoursePage';
import ArchitectsBlueprintPage from './pages/ArchitectsBlueprintPage';

const ADMIN_EMAILS = [
  'admin@theoperatormethod.com',
  'rmcmanus@ramdesignworks.com',
  'r0dmac522@gmail.com',
];

function isAdminEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return ADMIN_EMAILS.includes(normalized);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Requires auth AND a purchased tier, AND beta TOS if beta user.
function CourseRoute({ children }) {
  const { currentUser, userProfile, loading, isAdmin } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/purgatory" replace />;
  if (isAdmin) return children; // admin bypasses all requirements
  if (!userProfile?.tier) return <Navigate to="/pricing" replace />;
  if (userProfile?.betaTester && !userProfile?.betaTosAccepted) {
    return <Navigate to="/beta-terms" replace />;
  }
  return children;
}

// Admin-only gate: no UID → login (with redirect back). Wrong email → purgatory.
function AdminRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/login?redirect=/admin" replace />;
  if (!isAdminEmail(currentUser.email)) {
    return <Navigate to="/purgatory" replace />;
  }
  return children;
}

// Beta users who haven't accepted the Beta TOS are redirected to /beta-terms
function BetaTosRoute({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!currentUser) return <Navigate to="/purgatory" replace />;
  if (userProfile?.betaTester && !userProfile?.betaTosAccepted) {
    return <Navigate to="/beta-terms" replace />;
  }
  return children;
}

const BACK_ROUTES = {
  '/micro-assessment': '/architects-blueprint',
};

function GlobalFooter() {
  const location = useLocation();
  const navigate = useNavigate();
  const hideOnPaths = ['/login', '/v2-course'];
  if (hideOnPaths.includes(location.pathname)) return null;

  const backPath = BACK_ROUTES[location.pathname];

  const linkStyle = {
    color: 'rgba(153,179,214,0.55)',
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'color 0.2s',
  };

  return (
    <footer style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 999,
      padding: '10px 20px',
      background: 'linear-gradient(180deg, rgba(8,15,32,0) 0%, rgba(8,15,32,0.95) 40%)',
      backdropFilter: 'blur(6px)',
      borderTop: '1px solid rgba(79,142,247,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '6px 16px',
      fontSize: '10px',
      letterSpacing: '0.04em',
      color: 'rgba(153,179,214,0.4)',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {backPath && (
        <>
          <button onClick={() => navigate(backPath)} style={{ background: 'transparent', border: '1px solid rgba(153,179,214,0.3)', color: '#99b3d6', borderRadius: '9px', padding: '3px 12px', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>← Back</button>
          <span style={{ opacity: 0.3 }}>|</span>
        </>
      )}
      <a href="/terms" style={linkStyle}>Terms of Service</a>
      <span style={{ opacity: 0.3 }}>|</span>
      <a href="/privacy" style={linkStyle}>Privacy Policy</a>
      <span style={{ opacity: 0.3 }}>|</span>
      <a href="/about" style={linkStyle}>About</a>
      <span style={{ opacity: 0.3 }}>|</span>
      <span>
        Powered by{' '}
        <a href="https://ramdesignworks.com" target="_blank" rel="noopener noreferrer"
          style={{ ...linkStyle, color: 'rgba(79,142,247,0.6)' }}>
          RAMDESIGNWORKS
        </a>
      </span>
      <span style={{ opacity: 0.3 }}>|</span>
      <span>Resident AI Instructor: <strong style={{ color: 'rgba(153,179,214,0.55)' }}>OpenAI GPT-4o</strong></span>
      <span style={{ opacity: 0.3 }}>|</span>
      <span>Guest appearances by <strong style={{ color: 'rgba(153,179,214,0.55)' }}>OpenAI GPT-4o mini</strong></span>
      <span style={{ opacity: 0.3 }}>|</span>
      <span>Co-founded &amp; co-created with <strong style={{ color: 'rgba(153,179,214,0.55)' }}>GitHub Copilot</strong> &amp; <strong style={{ color: 'rgba(153,179,214,0.55)' }}>Anthropic Claude Opus 4.6</strong></span>
    </footer>
  );
}

function GlobalNavHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;
  const effectivePathname = backgroundLocation?.pathname || location.pathname;
  const { currentUser, isAdmin, userProfile, loading } = useAuth();
  const inAdminRoute = effectivePathname.startsWith('/admin');

  const hideOnPaths = ['/login'];
  if (hideOnPaths.includes(effectivePathname)) return null;

  // Don't render nav until auth has resolved — prevents "Login" flash for logged-in users
  if (loading) return null;

  // Anonymous users never see the nav — they use the login/landing page CTAs
  if (!currentUser) return null;

  function handleSignOut() {
    sessionStorage.setItem('om_intentional_signout', '1');
    signOut(auth).then(() => navigate('/login'));
  }

  return (
    <div style={{
      position: 'fixed',
      top: 8,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '7px 10px',
      borderRadius: 999,
      border: '1px solid rgba(79,142,247,0.26)',
      background: 'rgba(8,15,32,0.84)',
      backdropFilter: 'blur(8px)',
      boxShadow: '0 10px 28px rgba(1,6,20,0.34)',
    }}>
      <button onClick={() => navigate(-1)} style={headerStyles.btn}>← Back</button>
      {currentUser && <span style={headerStyles.emailPill}>{currentUser.email}</span>}
      {isAdmin && <button onClick={() => navigate('/admin')} style={headerStyles.btn}>Admin</button>}
      <button onClick={handleSignOut} style={headerStyles.btn}>Sign out</button>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = location.state?.backgroundLocation;
  const allowSignupModal = Boolean(backgroundLocation);
  const { currentUser, userProfile, loading, pendingIPVerification, clearPendingIPVerification } = useAuth();

  return (
    <>
      <GlobalNavHeader />
      {/* IP change verification modal — renders over everything else */}
      {pendingIPVerification && currentUser && (
        <IPVerificationModal
          user={currentUser}
          newIP={pendingIPVerification.newIP}
          currentIPChangeCount={userProfile?.ipChangeCount || 0}
          onVerified={() => clearPendingIPVerification()}
          onDismiss={() => clearPendingIPVerification()}
        />
      )}
      <Routes location={backgroundLocation || location}>
      {/* Public */}
      <Route path="/" element={<ArchitectsBlueprintPage />} />
      <Route path="/landing" element={<ArchitectsBlueprintPage />} />
      <Route path="/landing-legacy" element={<LandingPage />} />
      <Route path="/start" element={<StartPage />} />
      <Route path="/discover" element={<Navigate to="/create-profile?returnTo=%2Fchat&backTo=%2F" replace />} />
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/beta-terms" element={<BetaTosPage />} />
      <Route path="/security-doctrine" element={<SecurityDoctrinePage />} />
      <Route path="/login" element={loading ? <LoadingSpinner /> : (!currentUser ? <LoginPage /> : <Navigate to="/v2-course" replace />)} />
      <Route path="/signup" element={loading ? <LoadingSpinner /> : <SignupPage />} />
      <Route path="/assessment-intro" element={<Navigate to="/micro-assessment" replace />} />
      <Route path="/micro-assessment" element={<MicroAssessmentPage />} />
      <Route path="/placement-assessment" element={<PlacementAssessmentPage />} />
      <Route path="/results" element={<PlacementResultsPage />} />
      <Route path="/create-profile" element={<ProfileCreationPage />} />
      <Route path="/housekeeping-info" element={<AuthGuard><HousekeepingInfoPage /></AuthGuard>} />
      <Route path="/day-one" element={<DayOnePage />} />
      <Route path="/architects-blueprint" element={<ArchitectsBlueprintPage />} />

      {/* Protected — students (no UID → purgatory via AuthGuard) */}
      <Route path="/dashboard" element={<AuthGuard><Dashboard /></AuthGuard>} />
      <Route path="/placement" element={<Navigate to="/v2-course" replace />} />
      <Route path="/course/*" element={<Navigate to="/v2-course" replace />} />
      <Route path="/chat" element={<Navigate to="/v2-course" replace />} />
      <Route path="/session-report" element={<AuthGuard><SessionReport /></AuthGuard>} />

      {/* Admin only */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/v2-course" element={<AuthGuard><V2CoursePage /></AuthGuard>} />
      <Route path="/purgatory" element={<PurgatoryPage />} />
      <Route path="/video-test" element={<VideoTest />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

      {backgroundLocation && (
        <Routes>
          <Route
            path="/signup"
            element={loading ? <LoadingSpinner /> : <SignupPage />}
          />
        </Routes>
      )}
    </>
  );
}

const headerStyles = {
  emailPill: {
    padding: '4px 10px',
    borderRadius: 999,
    background: 'rgba(79,142,247,0.12)',
    color: '#7aadff',
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  modePill: {
    padding: '5px 10px',
    borderRadius: 999,
    border: '1px solid rgba(94,211,255,0.35)',
    background: 'rgba(14,37,67,0.72)',
    color: '#b9e9ff',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  },
  btn: {
    padding: '5px 10px',
    borderRadius: 999,
    border: '1px solid rgba(79,142,247,0.3)',
    background: 'rgba(18,32,61,0.78)',
    color: '#dceaff',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <GlobalFooter />
      </BrowserRouter>
    </AuthProvider>
  );
}
